import { Button, TextArea } from "react-aria-components";
import Pill from "../../../Pill";
import { useEffect, useState, useRef } from "react";
import NumberStepper from "../../../../lib/inputs/NumberStepper";
import { IPicklistItem } from "../../../List/RecallsPickList";
import { StatusDto } from "../../../../../enums/Domain/Status";
import { signal } from "@preact/signals-react";
import { RecallLineItemStatusSmartEnum } from "../../../../../enums/Domain/RecallLineItemStatusSmartEnum";
import { useKeyBind } from "../../../../../modules/keybinds/useKeyBind";
import KeyBinds, { KeyActions } from "../../../../../modules/keybinds/keyBindList";
import { Intent } from "../../../recallWizard/recallData";

export default function SidePanelRecallPickingItemInputs({ data, handleChange }: { data: IPicklistItem, handleChange: (input: string) => (id: string, e: string | number | boolean | StatusDto) => void; }) {
    // React state to manage the main data
    const [notes, setNotes] = useState<string>(data.notes);
    const quantityInputRef = useRef<HTMLInputElement>(null);

    // Signals to manage state for keybinding responsiveness
    const quantitySignal = signal(data.quantity);
    const selectedItemSignal = signal(data.id);

    useKeyBind(KeyBinds[KeyActions.Confirm], () => handleItemConfirmed(data.id));
    useKeyBind(KeyBinds[KeyActions.Disconfirm], () => handleItemNotFound(data.id));

    useKeyBind(KeyBinds[KeyActions.Increment], () => {
        const incremented = (quantitySignal.value ?? 0) + 1;
        handleQuantityChanged(incremented, selectedItemSignal.value);
    }, [data.id]);

    useKeyBind(KeyBinds[KeyActions.Decrement], () => {
        const decremented = Math.max((quantitySignal.value ?? 1) - 1, 0);
        handleQuantityChanged(decremented, selectedItemSignal.value);
    }, [data.id]);

    // Sync state and signals
    useEffect(() => {
        setNotes(data.notes);

        // Update the signals with the latest state values
        quantitySignal.value = Number(data.quantity);
        selectedItemSignal.value = data.id;
    }, [data]);

    function handleItemConfirmed(id: string) {
        if (data.quantity === null || data.quantity === undefined || data.quantity === 0) {
            handleQuantityChanged(1, id);
        }
    }

    function handleItemNotFound(id: string) {
        if (data.quantity !== 0) {
            handleQuantityChanged(0, id);
        }
    }

    function handleQuantityChanged(val: number, id: string) {
        quantitySignal.value = val; // Sync with signal
        handleChange("quantity")(id, val);
    }

    useEffect(() => {
        if (data.status.value === RecallLineItemStatusSmartEnum.Invalid.value) {
            const toPickStatus = new StatusDto(RecallLineItemStatusSmartEnum.ToPick.value, RecallLineItemStatusSmartEnum.getName());
            handleChange("status")(data.id, toPickStatus);
        }
    }, [data, handleChange]);

    // Auto-focus quantity input when item changes (e.g., when Next is clicked)
    useEffect(() => {
        if (quantityInputRef.current) {
            quantityInputRef.current.focus();
            quantityInputRef.current.select();
        }
    }, [data.id]);

    return (
        <div className="pick-list__side-panel-box">
            <div className="grid-x width--full">
                <p className="small small-3">Reference</p>
                <p className="small small-3">Contents</p>
                <p className="small small-3">Type</p>
                <p className="small small-3">Year</p>
            </div>
            <div>
                <div className="width--full grid-x alignItems--center">
                    <p className="small-3 textWrap">{data.reference}</p>
                    <p className="small-3 textWrap">{data.intentDisplayValue}</p>
                    <p className="small-3 textWrap">{data.type}</p>
                    <p className="small-3 textWrap">{data.year != 0 ? data.year : '--'}</p>
                </div>
                <div className="margin__top--xxs">
                    <p className="breadcrumb width--full">{data.breadcrumb}</p>
                </div>
            </div>
            <div>
                <div className="display--flex justifyContent--space-between">
                    <div className="display--flex gap--xs flex__direction--column margin__left--xs margin__top--xxs">
                        <div className="display--flex alignItems--center gap--xs">
                            <div 
                                slot="selection" 
                                onClick={() => handleItemConfirmed(data.id)}
                                tabIndex={-1}
                            >
                                <div className={`input-radio`}>
                                    <div className={`input-radio__transition ${data?.quantity !== undefined && data?.quantity !== null && data?.quantity > 0 ? "input-radio--selected" : "input-radio--unselected"}`}></div>
                                </div>
                            </div>
                            <p>Confirmed</p>
                        </div>
                        <div className="display--flex alignItems--center gap--xs">
                            <div 
                                slot="selection" 
                                onClick={() => handleItemNotFound(data.id)}
                                tabIndex={-1}
                            >
                                <div className={`input-radio`}>
                                    <div className={`input-radio__transition ${data?.quantity !== undefined && data?.quantity !== null && data?.quantity === 0 ? "input-radio--selected" : "input-radio--unselected"}`}></div>
                                </div>
                            </div>
                            <p>Not Found</p>
                        </div>
                    </div>
                    <div>
                        <NumberStepper 
                            ref={quantityInputRef}
                            value={data.quantity} 
                            Label="Qty:" 
                            OnChange={(x: number) => handleQuantityChanged(x, data.id)} 
                            step={1} 
                            min={0} 
                            max={data.intent === Intent.WholeBox ? 1 : undefined}
                            tabIndex={1}
                            autoFocus={true}
                        />
                    </div>
                </div>
                <div className="margin__top--xs margin__left--xs gap--xxs grid-x grid-margin-x">
                    <div className="small-6">
                        <p className="small">CellNass Notes</p>
                        <TextArea 
                            value={notes} 
                            onChange={(e) => {
                                setNotes(e.target.value); // Update React state
                                handleChange("notes")(data.id, e.target.value);
                            }} 
                            aria-label="CellNass Notes" 
                            className={"pick-list__side-panel-text-area"}
                            tabIndex={2}
                        />
                    </div>
                    <div className="small-6">
                        <p className="small">Customer Comments</p>
                        <TextArea 
                            value={data.requestorComments || ""} 
                            readOnly={true}
                            aria-label="Customer Comments" 
                            className={"pick-list__side-panel-text-area side-panel__text-area--readonly"}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
